import React, { createContext, useState, useContext, useEffect } from 'react';

// Create a context for the API key / trial state
const ApiKeyContext = createContext();

// Custom hook to use the API key context
export const useApiKey = () => useContext(ApiKeyContext);

// Provider component for the API key
export const ApiKeyProvider = ({ children }) => {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('anthropic_api_key') || '');
  const [isKeyValid, setIsKeyValid] = useState(() => !!localStorage.getItem('anthropic_api_key'));

  // Trial mode ("Try it out") uses the owner's key via the server. Persisted so
  // a reload keeps the user in the trial rather than bouncing them to the key
  // screen. `trialBlock` is an in-memory reason the trial can't continue right
  // now ('trial_exhausted' | 'owner_out_of_credits').
  const [trialMode, setTrialMode] = useState(() => localStorage.getItem('convocoach_trial') === 'true');
  const [trialBlock, setTrialBlockState] = useState(null);

  // Keep localStorage in sync with the API key.
  useEffect(() => {
    if (apiKey) {
      localStorage.setItem('anthropic_api_key', apiKey);
      setIsKeyValid(true);
    } else {
      localStorage.removeItem('anthropic_api_key');
      setIsKeyValid(false);
    }
  }, [apiKey]);

  // Saving your own key takes over from any trial.
  const saveApiKey = (key) => {
    setApiKey(key);
    setTrialMode(false);
    localStorage.removeItem('convocoach_trial');
    setTrialBlockState(null);
  };

  const clearApiKey = () => {
    setApiKey('');
    setIsKeyValid(false);
    localStorage.removeItem('anthropic_api_key');
  };

  const startTrial = () => {
    setTrialMode(true);
    localStorage.setItem('convocoach_trial', 'true');
    setTrialBlockState(null);
  };

  const endTrial = () => {
    setTrialMode(false);
    localStorage.removeItem('convocoach_trial');
    setTrialBlockState(null);
  };

  const setTrialBlock = (reason) => setTrialBlockState(reason);
  const clearTrialBlock = () => setTrialBlockState(null);

  const value = {
    apiKey,
    isKeyValid,
    saveApiKey,
    clearApiKey,
    trialMode,
    startTrial,
    endTrial,
    trialBlock,
    setTrialBlock,
    clearTrialBlock,
  };

  return (
    <ApiKeyContext.Provider value={value}>
      {children}
    </ApiKeyContext.Provider>
  );
};

export default ApiKeyContext;
